import { useEffect, useRef, useState, type ReactNode } from "react";
import { useParams } from "react-router";
import { Abweisung as DomaenenAbweisung } from "@dj/domain/gemeinsam";
import {
  entfernungAbgerundet, GENAUIGKEIT_OHNE_BESTAETIGUNG_M, GENAUIGKEIT_OHNE_WARNUNG_M, koordinatenLesen,
  plausibilitaet, STANDARTEN, standBezeichnung, standnummerGueltig, type Geoposition,
} from "@dj/domain/revier";
import { Ansicht } from "../design/Ansicht";
import { Dialog } from "../design/Dialog";
import { Auswahl, Textfeld } from "../design/Formular";
import { Abweisung, Meldung, Status } from "../design/Hinweis";
import { Knopf } from "../design/Kachel";
import { Symbol } from "../design/Symbol";
import { abweisungsgrund, befehl, umschlag } from "../plattform/api";
import { firebaseDienste } from "../plattform/firebase";
import { useSammlung, type Zeile } from "../plattform/lesemodelle";
import { klaerungRegistrieren } from "../plattform/Meldungen";
import { einreihen, entfernen, type Eintrag } from "../plattform/warteschlange";
import { useReviername } from "./Revier";

/** Filter der Liste (oberflaeche.md §7 „Stände“, V-30); Voreinstellung „in Betrieb“. */
const FILTER = ["in Betrieb", "stillgelegt"] as const;

/** Fester Hinweis bei einer Standnummer in falscher Form (oberflaeche.md §9, V-30, K106 (2)). */
export const HINWEIS_STANDNUMMER = "Standnummer: eine Zahl, optional mit einem Kleinbuchstaben (z. B. 7a).";

type Aktion = "Verlegen" | "Umbenennen" | "Stilllegen" | "Rote Zone";

/**
 * Stände des Reviers (oberflaeche.md §7 „Stände“): je Stand `article` „Stand <Nr>“ mit dem Text
 * `Stand <Nr> · <Standname>` und Verlegen, Umbenennen, Stilllegen (mit Bestätigung), Rote Zone; Filter „in Betrieb“ und
 * „stillgelegt“ (Löschen = Stilllegen, K106 (1)); „Vor Ort“ (GPS-Messung, F-020/F-021, ohne Netz über die
 * Warteschlange) und „Koordinaten“ (Eingabe wie in Google Maps mit Plausibilitätsprüfung vor dem Senden, F-022/F-023,
 * K21). Die Standnummer wird schon bei der Eingabe geprüft (V-30).
 */
export function Staende() {
  const { revierId = "" } = useParams();
  const name = useReviername(revierId);
  const liste = useSammlung(`reviere/${revierId}/staende`);
  const [filter, setFilter] = useState<string>(FILTER[0]);
  const [dialog, setDialog] = useState<null | "vorort" | "koordinaten">(null);
  const [aktion, setAktion] = useState<{ art: Aktion; stand: Zeile } | null>(null);
  const zeilen = [...liste.daten].sort((a, b) => String(a["Stand"]).localeCompare(String(b["Stand"]), "de", { numeric: true }));
  const inBetrieb = zeilen.filter((z) => z["Standstatus"] === "in Betrieb");
  const gezeigt = zeilen.filter((z) => z["Standstatus"] === filter);
  return (
    <Ansicht name="Stände" symbol="stand" unterzeile={name ?? undefined} unten={
      <div className="knopfreihe">
        <Knopf art="planung" symbol="standort" onPress={() => setDialog("vorort")}>Vor Ort</Knopf>
        <Knopf art="planung" symbol="karte" onPress={() => setDialog("koordinaten")}>Koordinaten</Knopf>
      </div>
    }>
      <Auswahl label="Filter" optionen={FILTER} wert={filter} onChange={setFilter} />
      <section className="liste-bereich platte" role="region" aria-label="Stände des Reviers">
        <h2 className="kopf"><Symbol name="stand" />Stände</h2>
        {gezeigt.map((z) => <StandEintrag key={z._id} z={z} onAktion={(art) => setAktion({ art, stand: z })} />)}
        {gezeigt.length === 0 && !liste.laedt ? (
          <div className="leer"><Symbol name="stand" /><span>{filter === "stillgelegt" ? "Keine stillgelegten Stände" : "Noch keine Stände"}</span></div>
        ) : null}
      </section>
      {dialog === "vorort" ? <VorOrtDialog revierId={revierId} onSchliessen={() => setDialog(null)} /> : null}
      {dialog === "koordinaten" ? <KoordinatenDialog revierId={revierId} staende={inBetrieb} onSchliessen={() => setDialog(null)} /> : null}
      {aktion?.art === "Verlegen" ? <VerlegenDialog stand={aktion.stand} onSchliessen={() => setAktion(null)} /> : null}
      {aktion?.art === "Umbenennen" ? <UmbenennenDialog stand={aktion.stand} onSchliessen={() => setAktion(null)} /> : null}
      {aktion?.art === "Stilllegen" ? <StilllegenDialog stand={aktion.stand} onSchliessen={() => setAktion(null)} /> : null}
      {aktion?.art === "Rote Zone" ? <RoteZoneDialog stand={aktion.stand} onSchliessen={() => setAktion(null)} /> : null}
    </Ansicht>
  );
}

function grad(n: unknown): string {
  return typeof n === "number" ? n.toFixed(6) : "—";
}

function standname(z: Zeile): string | null {
  return typeof z["Standname"] === "string" && z["Standname"] ? z["Standname"] : null;
}

/** Name des `article` bleibt `Stand <Nr>` (Vertrag §1.5); sichtbar steht `Stand <Nr> · <Standname>` (V-30). */
export function StandEintrag({ z, onAktion }: { z: Zeile; onAktion: (a: Aktion) => void }) {
  const nummer = String(z["Stand"]);
  const stillgelegt = z["Standstatus"] === "stillgelegt";
  const zonen = (z["Rote Zonen"] as Array<{ "rote Zone": string; von: number; bis: number }> | undefined) ?? [];
  return (
    <article className="zeile" aria-label={`Stand ${nummer}`}>
      <Symbol name="stand" />
      <div>
        <div className="zeile__t">{standBezeichnung(nummer, standname(z))}</div>{" "}
        <div className="zeile__u"><span className="mono">{grad(z["Breite"])}, {grad(z["Länge"])}</span>
          {typeof z["Genauigkeit"] === "number" ? <> · {String(z["Genauigkeit"])} m</> : null}
          {z["Standart"] ? <> · {String(z["Standart"])}</> : null}</div>
        {z["Zuweg"] ? <div className="zeile__u">{String(z["Zuweg"])}</div> : null}
        <div className="zeile__marken"><span className={stillgelegt ? "marke-status" : "marke-status marke-status--voll"}>{String(z["Standstatus"])}</span>
          {zonen.map((r) => <span key={r["rote Zone"]} className="marke-status">{r["rote Zone"]} {r.von}°–{r.bis}°</span>)}</div>
        {stillgelegt ? null : (
          <div className="knopfreihe">
            <Knopf art="planung-zweit" onPress={() => onAktion("Verlegen")}>Verlegen</Knopf>
            <Knopf art="planung-zweit" onPress={() => onAktion("Umbenennen")}>Umbenennen</Knopf>
            <Knopf art="planung-zweit" onPress={() => onAktion("Stilllegen")}>Stilllegen</Knopf>
            <Knopf art="planung-zweit" onPress={() => onAktion("Rote Zone")}>Rote Zone</Knopf>
          </div>
        )}
      </div>
    </article>
  );
}

/**
 * Standnummer mit Prüfung schon bei der Eingabe (V-30, K106 (2)): bei falscher Form der feste Hinweis als `status`
 * direkt unter dem Feld; leer zeigt keinen Hinweis (die Handlung bleibt trotzdem gesperrt, siehe `nummerGueltig`).
 */
export function Standnummerfeld({ wert, onChange, autoFocus }: { wert: string; onChange: (w: string) => void; autoFocus?: boolean }) {
  const falsch = wert.trim() !== "" && !standnummerGueltig(wert);
  return (
    <>
      <Textfeld label="Standnummer" wert={wert} onChange={onChange} eingabemodus="text" autoFocus={autoFocus} />
      {falsch ? <Status symbol="info">{HINWEIS_STANDNUMMER}</Status> : null}
    </>
  );
}

/** Daten des Standnamens für einen Anlege-Befehl: nur ein nicht leerer Name wird gesendet. */
function mitStandname(daten: Record<string, unknown>, name: string): Record<string, unknown> {
  return name.trim() ? { ...daten, Standname: name.trim() } : daten;
}

// -- Messung vor Ort ----------------------------------------------------------------------------------------------

interface Messung { breite: number; laenge: number; genauigkeit: number; zeit: Date }

/** Dauer der Messung (F-021: bis zu 30 Sekunden). */
const MESSDAUER_MS = 30_000;
/** Zeitgrenze einer einzelnen Positionsanfrage; „keine Position“ steht fest, wenn auch die letzte abgelaufen ist. */
const ANFRAGE_MS = 1_000;

/**
 * Laufende GPS-Messung: Angebot ist die beste (kleinste) Genauigkeit seit Beginn; nach 30 Sekunden ändert sich das
 * Angebot nicht mehr (oberflaeche.md §7 „Messung vor Ort“). Die Zeit einer Messung kommt aus `Date` (ADR-0030).
 */
function useMessung(): { angebot: Messung | null; beendet: boolean } {
  const [angebot, setAngebot] = useState<Messung | null>(null);
  const [beendet, setBeendet] = useState(false);
  const besteRef = useRef<Messung | null>(null);
  useEffect(() => {
    let aktiv = true;
    const beginn = Date.now();
    const neu = (p: GeolocationPosition) => {
      if (!aktiv || Date.now() - beginn > MESSDAUER_MS) return;
      const m = { breite: p.coords.latitude, laenge: p.coords.longitude, genauigkeit: Math.round(p.coords.accuracy), zeit: new Date() };
      if (!besteRef.current || m.genauigkeit < besteRef.current.genauigkeit) {
        besteRef.current = m;
        setAngebot(m);
      }
    };
    const optionen = { enableHighAccuracy: true, maximumAge: 0, timeout: ANFRAGE_MS };
    const geo = navigator.geolocation;
    const beobachtung = geo?.watchPosition(neu, () => undefined, optionen);
    const takt = window.setInterval(() => geo?.getCurrentPosition(neu, () => undefined, optionen), 1_000);
    const ende = window.setTimeout(() => { aktiv = false; window.clearInterval(takt); }, MESSDAUER_MS);
    const keine = window.setTimeout(() => setBeendet(true), MESSDAUER_MS + ANFRAGE_MS);
    return () => {
      aktiv = false;
      if (beobachtung !== undefined) geo?.clearWatch(beobachtung);
      window.clearInterval(takt);
      window.clearTimeout(ende);
      window.clearTimeout(keine);
    };
  }, []);
  return { angebot, beendet };
}

function VorOrtDialog({ revierId, onSchliessen }: { revierId: string; onSchliessen: () => void }) {
  const [nummer, setNummer] = useState("");
  const [name, setName] = useState("");
  const [bestaetigen, setBestaetigen] = useState<Messung | null>(null);
  const { angebot, beendet } = useMessung();
  const nummerGueltig = standnummerGueltig(nummer);

  async function erfassen(m: Messung, bestaetigt: boolean) {
    const daten: Record<string, unknown> = mitStandname({
      Revier: revierId, Standnummer: nummer.trim(), Breite: m.breite, "Länge": m.laenge, Genauigkeit: m.genauigkeit,
      Erfassungszeitpunkt: m.zeit.toISOString(),
    }, name);
    if (bestaetigt) daten["Genauigkeit bestätigt"] = true;
    onSchliessen();
    await einreihen("StandVorOrtAnlegen", `Stand ${nummer.trim()}`, umschlag(daten));
  }

  function uebernehmen() {
    if (!angebot || !nummerGueltig) return;
    if (angebot.genauigkeit > GENAUIGKEIT_OHNE_BESTAETIGUNG_M) setBestaetigen(angebot);
    else void erfassen(angebot, false);
  }

  return (
    <Dialog name="Vor Ort" symbol="standort" offen onSchliessen={onSchliessen}>
      <div className="formular">
        <Standnummerfeld wert={nummer} onChange={setNummer} autoFocus />
        <Textfeld label="Standname" wert={name} onChange={setName} />
        {angebot ? <Status symbol="standort">{`Genauigkeit ${angebot.genauigkeit} m`}</Status> : null}
        {angebot && angebot.genauigkeit > GENAUIGKEIT_OHNE_WARNUNG_M ? <Status symbol="standort">Ungenaue Position</Status> : null}
        {!angebot && beendet ? <Meldung>Keine Position verfügbar.</Meldung> : null}
        <div className="knopfreihe">
          {angebot ? <Knopf onPress={uebernehmen} isDisabled={!nummerGueltig}>Übernehmen</Knopf> : null}
          <Knopf art="zweit" onPress={onSchliessen}>Abbrechen</Knopf>
        </div>
      </div>
      {bestaetigen ? (
        <Dialog name="Genauigkeit" symbol="standort" offen bestaetigung onSchliessen={() => { setBestaetigen(null); onSchliessen(); }}>
          <p>{`Genauigkeit ${bestaetigen.genauigkeit} m bestätigen?`}</p>
          <div className="knopfreihe">
            <Knopf onPress={() => void erfassen(bestaetigen, true)}>Bestätigen</Knopf>
            <Knopf art="zweit" onPress={() => { setBestaetigen(null); onSchliessen(); }}>Abbrechen</Knopf>
          </div>
        </Dialog>
      ) : null}
    </Dialog>
  );
}

// -- Koordinaten ---------------------------------------------------------------------------------------------------

function plattformwert(name: string, sonst: number): number {
  try {
    const w = firebaseDienste().konfig.plattform[name];
    return typeof w === "number" ? w : sonst;
  } catch {
    return sonst;
  }
}

function positionen(staende: readonly Zeile[]): Geoposition[] {
  return staende.filter((z) => typeof z["Breite"] === "number" && typeof z["Länge"] === "number")
    .map((z) => ({ breite: z["Breite"] as number, laenge: z["Länge"] as number }));
}

interface Warnung { km: number; vertauscht: boolean }

function KoordinatenDialog({ revierId, staende, onSchliessen }: { revierId: string; staende: readonly Zeile[]; onSchliessen: () => void }) {
  const [nummer, setNummer] = useState("");
  const [name, setName] = useState("");
  const [eingabe, setEingabe] = useState("");
  const [standart, setStandart] = useState<string | null>(null);
  const [zuweg, setZuweg] = useState("");
  const [warnung, setWarnung] = useState<Warnung | null>(null);
  const [grund, setGrund] = useState<string | null>(null);

  async function senden() {
    setWarnung(null);
    const daten = mitStandname({ Revier: revierId, Standnummer: nummer.trim(), Koordinaten: eingabe.trim() }, name);
    if (standart) daten["Standart"] = standart;
    if (zuweg.trim()) daten["Zuweg"] = zuweg.trim();
    const a = await befehl("StandMitKoordinatenAnlegen", daten);
    if (a.ok) onSchliessen();
    else setGrund(abweisungsgrund(a));
  }

  function speichern() {
    if (!standnummerGueltig(nummer)) return;
    setGrund(null);
    let p: Geoposition;
    try {
      p = koordinatenLesen(eingabe);
    } catch (e) {
      if (e instanceof DomaenenAbweisung) { void senden(); return; }
      throw e;
    }
    const warnungKm = plattformwert("Warnung ab einer Entfernung zum nächsten Stand von", 1);
    const tauschKm = plattformwert("Hinweis auf Vertauschung, wenn die getauschte Position näher am nächsten Stand läge als", 1);
    const pl = plausibilitaet(p, positionen(staende), warnungKm, tauschKm);
    if (pl.warnung && pl.entfernungKm !== null) setWarnung({ km: entfernungAbgerundet(pl.entfernungKm, warnungKm), vertauscht: pl.vertauscht });
    else void senden();
  }

  return (
    <Dialog name="Koordinaten" symbol="karte" offen onSchliessen={onSchliessen}>
      <div className="formular">
        <Standnummerfeld wert={nummer} onChange={setNummer} autoFocus />
        <Textfeld label="Standname" wert={name} onChange={setName} />
        <Textfeld label="Koordinaten" wert={eingabe} onChange={setEingabe} mono hilfe="Wie in Google Maps, z. B. 52.5200, 13.4050" />
        <Auswahl label="Standart" optionen={STANDARTEN} wert={standart} onChange={setStandart} />
        <Textfeld label="Zuweg" wert={zuweg} onChange={setZuweg} />
        <Abweisung grund={grund} />
        <div className="knopfreihe">
          <Knopf onPress={speichern} isDisabled={!standnummerGueltig(nummer)}>Speichern</Knopf>
          <Knopf art="zweit" onPress={onSchliessen}>Abbrechen</Knopf>
        </div>
      </div>
      {warnung ? (
        <Dialog name="Warnung" symbol="standort" offen bestaetigung onSchliessen={() => setWarnung(null)}>
          <p>{`Die Position liegt mehr als ${warnung.km} km vom nächsten Stand entfernt.`}</p>
          {warnung.vertauscht ? <p>Breite und Länge sind möglicherweise vertauscht.</p> : null}
          <div className="knopfreihe">
            <Knopf onPress={() => void senden()}>Bestätigen</Knopf>
            <Knopf art="zweit" onPress={() => setWarnung(null)}>Abbrechen</Knopf>
          </div>
        </Dialog>
      ) : null}
    </Dialog>
  );
}

// -- Pflege eines Standes ------------------------------------------------------------------------------------------

function Formulardialog({ name, symbol, onSpeichern, onSchliessen, grund, gesperrt, children }: {
  name: string; symbol: string; onSpeichern: () => void; onSchliessen: () => void; grund: string | null; gesperrt?: boolean; children: ReactNode;
}) {
  return (
    <Dialog name={name} symbol={symbol} offen onSchliessen={onSchliessen}>
      <div className="formular">
        {children}
        <Abweisung grund={grund} />
        <div className="knopfreihe">
          <Knopf onPress={onSpeichern} isDisabled={gesperrt}>Speichern</Knopf>
          <Knopf art="zweit" onPress={onSchliessen}>Abbrechen</Knopf>
        </div>
      </div>
    </Dialog>
  );
}

function VerlegenDialog({ stand, onSchliessen }: { stand: Zeile; onSchliessen: () => void }) {
  const [eingabe, setEingabe] = useState("");
  const [grund, setGrund] = useState<string | null>(null);
  async function speichern() {
    const a = await befehl("StandVerlegen", { Stand: stand._id, Koordinaten: eingabe.trim() });
    if (a.ok) onSchliessen(); else setGrund(abweisungsgrund(a));
  }
  return (
    <Formulardialog name="Verlegen" symbol="stand" onSpeichern={speichern} onSchliessen={onSchliessen} grund={grund}>
      <Textfeld label="Koordinaten" wert={eingabe} onChange={setEingabe} mono autoFocus />
    </Formulardialog>
  );
}

/**
 * Umbenennen (V-30): Standnummer und Standname; je geändertem Feld ein Befehl, zuerst die Nummer. Ein leerer Standname
 * entfernt ihn. Wird ein Befehl abgewiesen, bleibt der Dialog mit der Eingabe offen.
 */
function UmbenennenDialog({ stand, onSchliessen }: { stand: Zeile; onSchliessen: () => void }) {
  const bisherNummer = String(stand["Stand"] ?? "");
  const bisherName = standname(stand) ?? "";
  const [nummer, setNummer] = useState(bisherNummer);
  const [name, setName] = useState(bisherName);
  const [grund, setGrund] = useState<string | null>(null);
  async function speichern() {
    if (!standnummerGueltig(nummer)) return;
    setGrund(null);
    if (nummer.trim() !== bisherNummer) {
      const a = await befehl("StandUmbenennen", { Stand: stand._id, Standnummer: nummer.trim() });
      if (!a.ok) { setGrund(abweisungsgrund(a)); return; }
    }
    if (name.trim() !== bisherName) {
      const a = await befehl("StandnameAendern", { Stand: stand._id, Standname: name.trim() || null });
      if (!a.ok) { setGrund(abweisungsgrund(a)); return; }
    }
    onSchliessen();
  }
  return (
    <Formulardialog name="Umbenennen" symbol="stand" onSpeichern={() => void speichern()} onSchliessen={onSchliessen} grund={grund}
      gesperrt={!standnummerGueltig(nummer)}>
      <Standnummerfeld wert={nummer} onChange={setNummer} autoFocus />
      <Textfeld label="Standname" wert={name} onChange={setName} />
    </Formulardialog>
  );
}

/** Stilllegen mit Bestätigung (V-30, K106 (1)); eine Abweisung steht im `alertdialog`, der dann offen bleibt. */
function StilllegenDialog({ stand, onSchliessen }: { stand: Zeile; onSchliessen: () => void }) {
  const [grund, setGrund] = useState<string | null>(null);
  async function bestaetigen() {
    const a = await befehl("StandStilllegen", { Stand: stand._id });
    if (a.ok) onSchliessen(); else setGrund(abweisungsgrund(a));
  }
  return (
    <Dialog name="Stilllegen" symbol="stand" offen bestaetigung onSchliessen={onSchliessen}>
      <p>{`${standBezeichnung(String(stand["Stand"]), standname(stand))} stilllegen?`}</p>
      <p>Der Stand bleibt in früheren Drückjagden erhalten und kann nicht mehr zugewiesen werden.</p>
      <Abweisung grund={grund} />
      <div className="knopfreihe">
        <Knopf onPress={() => void bestaetigen()}>Bestätigen</Knopf>
        <Knopf art="zweit" onPress={onSchliessen}>Abbrechen</Knopf>
      </div>
    </Dialog>
  );
}

/** Kompassrichtung des Geräts (`deviceorientationabsolute`, iOS `webkitCompassHeading`), ganze Grad 0–359. */
function useKompass(): number | null {
  const [richtung, setRichtung] = useState<number | null>(null);
  useEffect(() => {
    const neu = (e: DeviceOrientationEvent) => {
      const ios = (e as DeviceOrientationEvent & { webkitCompassHeading?: number }).webkitCompassHeading;
      // relative Ausrichtung (ohne Nordbezug) ist keine Kompassrichtung
      const absolut = e.type === "deviceorientationabsolute" || e.absolute;
      const r = typeof ios === "number" ? ios : absolut && typeof e.alpha === "number" ? (360 - e.alpha) % 360 : null;
      if (r !== null) setRichtung(Math.round(r) % 360);
    };
    window.addEventListener("deviceorientationabsolute", neu as EventListener);
    window.addEventListener("deviceorientation", neu as EventListener);
    // Generic Sensor API: Gierwinkel aus dem Quaternion, im Uhrzeigersinn ab Nord
    type Sensor = { quaternion?: number[]; start(): void; stop(): void; addEventListener(t: string, f: () => void): void };
    const Klasse = (window as unknown as { AbsoluteOrientationSensor?: new (o: { frequency: number }) => Sensor }).AbsoluteOrientationSensor;
    let sensor: Sensor | null = null;
    if (Klasse) {
      try {
        sensor = new Klasse({ frequency: 10 });
        sensor.addEventListener("reading", () => {
          const q = sensor?.quaternion;
          if (!q || q.length < 4) return;
          const [x, y, z, w] = q as [number, number, number, number];
          const alpha = (Math.atan2(2 * (w * z + x * y), 1 - 2 * (y * y + z * z)) * 180) / Math.PI;
          setRichtung(Math.round((360 - alpha + 360) % 360) % 360);
        });
        sensor.start();
      } catch {
        sensor = null;
      }
    }
    return () => {
      sensor?.stop();
      window.removeEventListener("deviceorientationabsolute", neu as EventListener);
      window.removeEventListener("deviceorientation", neu as EventListener);
    };
  }, []);
  return richtung;
}

function RoteZoneDialog({ stand, onSchliessen }: { stand: Zeile; onSchliessen: () => void }) {
  const [bezeichnung, setBezeichnung] = useState("");
  const [von, setVon] = useState("");
  const [bis, setBis] = useState("");
  const [grund, setGrund] = useState<string | null>(null);
  const kompass = useKompass();
  async function uebernehmen() {
    const daten: Record<string, unknown> = { Stand: stand._id, von: Number(von), bis: Number(bis) };
    if (bezeichnung.trim()) daten["Bezeichnung"] = bezeichnung.trim();
    const a = await befehl("RoteZoneFestlegen", daten);
    if (a.ok) onSchliessen(); else setGrund(abweisungsgrund(a));
  }
  return (
    <Dialog name="Rote Zone" symbol="sperre" offen onSchliessen={onSchliessen}>
      <div className="formular">
        <Textfeld label="Bezeichnung" wert={bezeichnung} onChange={setBezeichnung} autoFocus />
        {kompass !== null ? <Status symbol="navigation">{`${kompass}°`}</Status> : null}
        <div className="knopfreihe">
          <Knopf art="planung-zweit" isDisabled={kompass === null} onPress={() => setVon(String(kompass))}>von</Knopf>
          <Knopf art="planung-zweit" isDisabled={kompass === null} onPress={() => setBis(String(kompass))}>bis</Knopf>
        </div>
        <Textfeld label="von" wert={von} onChange={setVon} eingabemodus="numeric" />
        <Textfeld label="bis" wert={bis} onChange={setBis} eingabemodus="numeric" />
        <Abweisung grund={grund} />
        <div className="knopfreihe">
          <Knopf onPress={uebernehmen}>Übernehmen</Knopf>
          <Knopf art="zweit" onPress={onSchliessen}>Abbrechen</Knopf>
        </div>
      </div>
    </Dialog>
  );
}

// -- Klärung einer abgewiesenen Messung (oberflaeche.md §10) --------------------------------------------------------

/** Die gemessene Position bleibt erhalten; „Anlegen“ erfasst dieselbe Messung unter einer anderen Standnummer. */
function MessungKlaeren({ e }: { e: Eintrag }) {
  const [nummer, setNummer] = useState("");
  const d = e.daten;
  async function anlegen() {
    if (!standnummerGueltig(nummer)) return;
    const neu = nummer.trim();
    await einreihen("StandVorOrtAnlegen", `Stand ${neu}`, umschlag({ ...d, Standnummer: neu }));
    await entfernen(e.meldungsId);
  }
  return (
    <div className="formular">
      <div className="zeile__u"><span className="mono">{grad(d["Breite"])}, {grad(d["Länge"])}</span> · {`${String(d["Genauigkeit"])} m`}</div>
      <Standnummerfeld wert={nummer} onChange={setNummer} />
      <Knopf art="planung" symbol="stand" onPress={anlegen} isDisabled={!standnummerGueltig(nummer)}>Anlegen</Knopf>
    </div>
  );
}

klaerungRegistrieren("StandVorOrtAnlegen", (e) => <MessungKlaeren e={e} />);
